import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Avatar, AvatarGroup, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, FormControlLabel, Grid2 as Grid, Link, Skeleton, Stack, Switch, Tooltip,
  Typography,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  LocationOn as LocationIcon,
  Language as OnlineIcon,
  OpenInNew as ExternalIcon,
  Groups as GroupsIcon,
  PersonAdd as InviteIcon,
  Edit as EditIcon,
  EventBusy as CancelIcon,
  Lock as LockIcon,
  Videocam as VideoIcon,
  Assignment as DeadlineIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useMyLabs } from 'shared/hooks/useMyLabs';
import BackButton from 'components/common/buttons/BackButton';
import RsvpButton from './RsvpButton';
import AttendeesDialog from './AttendeesDialog';
import InviteDialog from './InviteDialog';
import EventFormDialog from './EventFormDialog';
import { STATUS_COLORS, connectionsLine, formatWhen, typeColor } from './eventMeta';

function Line({ icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, color: 'text.secondary' }}>
      {React.cloneElement(icon, { sx: { fontSize: 20, mt: 0.15 } })}
      <Typography variant="body2" component="div" color="text.primary" sx={{ minWidth: 0 }}>{children}</Typography>
    </Box>
  );
}

const RANK_LABELS = { core: 'CORE', ccf: 'CCF', thcpl: 'THCPL' };

export default function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [guests, setGuests] = useState(null);         // tab of the guest list to open
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);       // 'cancel' | 'restore' | 'delete'
  const [busy, setBusy] = useState(false);
  const labs = useMyLabs();

  const setSharing = async (share) => {
    try {
      const { data } = await axiosService.post(`/events/${event.id}/sharing/`, { share_with_labs: share });
      setEvent(data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not change who sees it.'));
    }
  };

  const load = useCallback(() => {
    setError(null);
    axiosService.get(`/events/${eventId}/`)
      .then(({ data }) => setEvent(data))
      .catch((err) => setError(err.response?.status === 404
        ? 'This event does not exist, or it is only for the people invited to it.'
        : apiErrorMessage(err, 'Could not load the event.')));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const act = async () => {
    setBusy(true);
    try {
      if (confirm === 'delete') {
        await axiosService.delete(`/events/${event.id}/`);
        navigate('/events/');
        return;
      }
      const { data } = await axiosService.patch(`/events/${event.id}/`, { status: confirm === 'cancel' ? 'cancelled' : 'upcoming' });
      setEvent(data);
      setConfirm(null);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !event) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
        <BackButton position="inline" /> 
        <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>
      </Box>
    );
  }
  if (!event) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
        <Skeleton variant="rounded" height={180} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={260} />
      </Box>
    );
  }

  const color = typeColor(event.event_type);
  const ranks = Object.entries(event.metadata?.rank || {}).filter(([k, v]) => v && RANK_LABELS[k]);
  const deadlines = (event.metadata?.deadlines || []).filter((d) => d.deadline && d.deadline !== 'TBD');
  const group = event.lab_info ? `${event.lab_info.name} members` : event.consortium_info ? `${event.consortium_info.name} members` : null;
  const audience = event.visibility === 'private' ? 'Invited people only' : group;
  const canInvite = event.status !== 'cancelled' && event.status !== 'past'
    && (event.can_edit || (event.visibility === 'public' && ['going', 'interested'].includes(event.my_rsvp)));
  const line = connectionsLine(event);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
              <Box onClick={() => navigate(-1)}
               sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, mb: 1.5, cursor: 'pointer', color: 'text.secondary',
                 '&:hover': { color: 'primary.main' } }}>
               <BackButton position="inline" onClick={() => {}} sx={{ color: 'inherit', '&:hover': { color: 'inherit' } }} />
               <Typography variant="body1" fontWeight={500} sx={{ color: 'inherit' }}>Back</Typography>
             </Box>


      <Card elevation={0} sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ height: 8, background: color }} />
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Chip size="small" label={event.event_type_display} sx={{ bgcolor: color, color: '#fff', fontWeight: 600 }} />
            <Chip size="small" label={event.status_display} color={STATUS_COLORS[event.status] || 'default'} variant="outlined" />
            {audience && <Chip size="small" icon={<LockIcon sx={{ fontSize: 14 }} />} label={audience} variant="outlined" />}
            {ranks.map(([k, v]) => <Chip key={k} size="small" label={`${RANK_LABELS[k]} ${v}`} variant="outlined" color="secondary" />)}
          </Box>

          <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.2, mb: 0.5, wordBreak: 'break-word' }}>
            {event.title}
          </Typography>
          {event.host && (
            <Typography variant="body2" color="text.secondary">
              Hosted by{' '}
              <Link component="button" underline="hover" onClick={() => navigate(`/profile/${event.host.id}/`)}>
                {event.host.name}
              </Link>
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 2.5 }}>
            <RsvpButton event={event} onChange={setEvent} size="medium" />
            {canInvite && (
              <Button variant="outlined" startIcon={<InviteIcon />} onClick={() => setInviteOpen(true)} sx={{ textTransform: 'none' }}>
                Invite
              </Button>
            )}
            {event.can_edit && (
              <>
                <Button variant="outlined" color="inherit" startIcon={<EditIcon />} onClick={() => setEditOpen(true)} sx={{ textTransform: 'none' }}>
                  Edit
                </Button>
                {event.status === 'cancelled' ? (
                  <Button color="inherit" onClick={() => setConfirm('restore')} sx={{ textTransform: 'none' }}>Restore</Button>
                ) : event.status !== 'past' && (
                  <Button color="error" startIcon={<CancelIcon />} onClick={() => setConfirm('cancel')} sx={{ textTransform: 'none' }}>
                    Cancel event
                  </Button>
                )}
                <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirm('delete')} sx={{ textTransform: 'none' }}>
                  Delete
                </Button>
              </>
            )}
          </Box>
          {labs.length > 0 && ['going', 'interested'].includes(event.my_rsvp) && (
            <Tooltip title={`Your lab${labs.length > 1 ? 's' : ''} (${labs.map((l) => l.name).join(', ')}) see${labs.length > 1 ? '' : 's'} on the lab calendar that you are ${event.my_rsvp === 'going' ? 'going' : 'interested'}. Switch off to keep it to yourself.`}>
              <FormControlLabel sx={{ mt: 1.5, ml: 0 }}
                control={<Switch size="small" checked={event.my_share_with_labs !== false}
                  onChange={(e) => setSharing(e.target.checked)} />}
                label={<Typography variant="body2" color="text.secondary">Show on my lab{labs.length > 1 ? 's\'' : '\'s'} calendar</Typography>} />
            </Tooltip>
          )}
          {event.my_rsvp === 'invited' && (
            <Alert severity="info" sx={{ mt: 2 }}>You were invited. Let the host know whether you are coming.</Alert>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        </CardContent>
      </Card>

      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Details</Typography>
              <Stack spacing={1.5}>
                <Line icon={<CalendarIcon />}>{formatWhen(event.start_date, event.end_date)}</Line>
                {event.location && <Line icon={<LocationIcon />}>{event.location}</Line>}
                {event.is_online && (
                  <Line icon={<OnlineIcon />}>
                    Online
                    {event.online_url && (
                      <Button size="small" startIcon={<VideoIcon />} href={event.online_url} target="_blank"
                        rel="noopener noreferrer" sx={{ ml: 1, textTransform: 'none' }}>
                        Join the call
                      </Button>
                    )}
                  </Line>
                )}
                {event.organizer && <Line icon={<GroupsIcon />}>{event.organizer}</Line>}
                {event.submission_deadline && (
                  <Line icon={<DeadlineIcon />}>
                    Submission deadline: {new Date(event.submission_deadline).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {deadlines.length > 1 && ` (${deadlines.length} rounds)`}
                  </Line>
                )}
                {event.website && (
                  <Line icon={<ExternalIcon />}>
                    <Link href={event.website} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                      {event.website}
                    </Link>
                  </Line>
                )}
              </Stack>
              {event.description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{event.description}</Typography>
                </>
              )}
              {event.categories?.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 2 }}>
                  {event.categories.map((c) => <Chip key={c.id} label={c.name} size="small" variant="outlined" />)}
                </Box>
              )}
              {event.source !== 'user' && event.source !== 'excel' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Imported from {event.source === 'ccfddl' ? 'ccf-deadlines' : 'Wikidata'}. Check dates on the event website.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight={700}>Guests</Typography>
                <Link component="button" underline="hover" variant="body2" onClick={() => setGuests('going')}>See all</Link>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, my: 1.5 }}>
                <Box sx={{ cursor: 'pointer' }} onClick={() => setGuests('going')}>
                  <Typography variant="h5" fontWeight={700}>{event.going_count}</Typography>
                  <Typography variant="caption" color="text.secondary">Going</Typography>
                </Box>
                <Box sx={{ cursor: 'pointer' }} onClick={() => setGuests('interested')}>
                  <Typography variant="h5" fontWeight={700}>{event.interested_count}</Typography>
                  <Typography variant="caption" color="text.secondary">Interested</Typography>
                </Box>
              </Box>
              {event.going_preview?.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => setGuests('going')}>
                  <AvatarGroup max={5} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, fontSize: 14 } }}>
                    {event.going_preview.map((p) => <Avatar key={p.id} src={p.avatar_url || undefined} alt={p.name}>{p.name[0]}</Avatar>)}
                  </AvatarGroup>
                  {line && <Typography variant="body2" color="text.secondary">{line}</Typography>}
                </Box>
              )}
              {event.going_count === 0 && event.interested_count === 0 && (
                <Typography variant="body2" color="text.secondary">Be the first to answer.</Typography>
              )}
              {event.can_edit && (
                <Link component="button" underline="hover" variant="body2" sx={{ mt: 1.5, display: 'block' }}
                  onClick={() => setGuests('invited')}>
                  Who was invited, who can't go
                </Link>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <AttendeesDialog open={Boolean(guests)} onClose={() => setGuests(null)} event={event} initialTab={guests || 'going'} />
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} event={event} onInvited={(e) => e && setEvent(e)} />
      <EventFormDialog open={editOpen} onClose={() => setEditOpen(false)} event={event} onSaved={setEvent} />

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
        <DialogTitle>
          {confirm === 'cancel' ? 'Cancel this event?' : confirm === 'delete' ? 'Delete this event?' : 'Restore this event?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm === 'cancel' && 'People going or interested get a notification. You can restore it later.'}
            {confirm === 'delete' && 'The event and its guest list are removed for everyone. This cannot be undone. Cancelling instead tells your guests.'}
            {confirm === 'restore' && 'The event is shown as planned again.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Back</Button>
          <Button variant="contained" color={confirm === 'restore' ? 'primary' : 'error'} onClick={act} disabled={busy}>
            {confirm === 'cancel' ? 'Cancel event' : confirm === 'delete' ? 'Delete' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
